import { useCallback, useEffect, useState } from 'react';

import { Badge, Button, Card, Icon } from '@ds/core';
import { DataTable } from '@ds/data';
import type { DataTableColumn } from '@ds/data';
import { Alert, Modal } from '@ds/feedback';
import { Checkbox, FormField, Input } from '@ds/forms';
import { useAviso } from '@/comuns/toast';
import { PERMISSOES_DA_PLATAFORMA, operadorTem } from '@/data/permissoesDaPlataforma';
import { dados } from '@/data/porta';
import { atual, cadastrarTotp, confirmarTotp, descartarFator, estadoDoMfa } from '@/session/sessao';
import type { CadastroDeTotp, EstadoDoMfa } from '@/session/tipos';
import type { OperadorDaPlataforma } from '@/data/tipos';
import { quandoRelativo } from './Visao';
import { ExigenciaDeMfa } from './ExigenciaDeMfa';

/**
 * A equipe que opera a VecchIA: quem pode o quê, e quem protegeu a conta.
 *
 * O MFA vem do servidor de autenticação, não de uma coluna nossa. As travas de
 * permissão são do banco — ninguém muda a própria, e ninguém concede o que não
 * tem —; a tela só desliga a caixa que o banco recusaria.
 */
export function EquipeDaPlataforma() {
    const [operadores, setOperadores] = useState<OperadorDaPlataforma[] | null>(null);
    const [erro, setErro] = useState<string | null>(null);
    const [editando, setEditando] = useState<OperadorDaPlataforma | null>(null);

    const carregar = useCallback(async () => {
        try {
            setOperadores(await dados().operadoresDaPlataforma());
            setErro(null);
        } catch (e) {
            setErro(e instanceof Error ? e.message : 'Não foi possível ler a equipe.');
        }
    }, []);

    useEffect(() => { void carregar(); }, [carregar]);

    const colunas: DataTableColumn<OperadorDaPlataforma & { id: string }>[] = [
        {
            key: 'nome', header: 'Pessoa',
            render: (o) => (
                <span>
                    <strong style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600 }}>
                        {o.nome}
                        {o.souEu && <Badge tone="neutral">você</Badge>}
                    </strong>
                    <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{o.email}</span>
                </span>
            ),
        },
        {
            key: 'permissoes', header: 'Permissões', width: 220,
            render: (o) => (o.permissoes.includes('*')
                ? <Badge tone="warning">acesso total</Badge>
                : `${o.permissoes.length} ${o.permissoes.length === 1 ? 'permissão' : 'permissões'}`),
        },
        {
            key: 'temMfa', header: 'MFA', width: 130,
            render: (o) => <Badge tone={o.temMfa ? 'success' : 'warning'} dot>{o.temMfa ? 'Ativo' : 'Sem MFA'}</Badge>,
        },
        { key: 'ultimoAcesso', header: 'Último acesso', align: 'right', width: 140, render: (o) => quandoRelativo(o.ultimoAcesso) },
        {
            key: 'acoes', header: '', align: 'right', width: 170,
            render: (o) => (o.souEu
                ? <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>não se muda a própria</span>
                : <Button size="sm" variant="secondary" onClick={() => setEditando(o)}>Editar permissões</Button>),
        },
    ];

    const semMfa = (operadores ?? []).filter((o) => !o.temMfa);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {erro && <Alert tone="danger" dismissible={false}>{erro}</Alert>}

            {operadores !== null && <ExigenciaDeMfa semMfa={semMfa.map((o) => o.nome)} />}

            <Card title="Equipe da plataforma" subtitle="Quem opera a Fórmula MED e o que pode fazer" padding={0}>
                <DataTable
                    columns={colunas}
                    rows={(operadores ?? []).map((o) => ({ ...o, id: o.usuarioId }))}
                    rowKey="id"
                    stickyHeader={false} style={{ border: 0, borderRadius: 0 }}
                    emptyMessage={operadores === null ? 'Lendo a equipe…' : 'Nenhum operador visível.'}
                />
            </Card>

            <MeuMfa />

            <PermissoesModal
                operador={editando}
                onFechar={() => setEditando(null)}
                onSalvo={(lista) => { setOperadores(lista); setEditando(null); }}
            />
        </div>
    );
}

function PermissoesModal({ operador, onFechar, onSalvo }: {
    operador: OperadorDaPlataforma | null;
    onFechar: () => void;
    onSalvo: (lista: OperadorDaPlataforma[]) => void;
}) {
    const aviso = useAviso();
    const [marcadas, setMarcadas] = useState<string[]>([]);
    const [salvando, setSalvando] = useState(false);
    const [erro, setErro] = useState<string | null>(null);
    const minhas = atual()?.permissoes ?? [];

    useEffect(() => {
        if (operador) {
            setMarcadas([...operador.permissoes]);
            setErro(null);
        }
    }, [operador]);

    const alternar = (id: string, ligada: boolean) =>
        setMarcadas((lista) => (ligada ? [...new Set([...lista, id])] : lista.filter((p) => p !== id)));

    const mudou = operador ? [...marcadas].sort().join() !== [...operador.permissoes].sort().join() : false;

    const salvar = async () => {
        if (!operador) return;
        setSalvando(true);
        setErro(null);
        try {
            const lista = await dados().definirPermissoesDoOperador(operador.usuarioId, marcadas);
            aviso({ title: 'Permissões salvas', description: `${operador.nome} · registrado na auditoria` });
            onSalvo(lista);
        } catch (e) {
            setErro(e instanceof Error ? e.message : 'Não foi possível salvar.');
        } finally {
            setSalvando(false);
        }
    };

    const grupos = [...new Set(PERMISSOES_DA_PLATAFORMA.map((p) => p.grupo))];

    return (
        <Modal
            open={operador !== null} onClose={onFechar} width={560}
            title={operador ? `Permissões de ${operador.nome}` : 'Permissões'}
            description="O que a pessoa vê e muda no painel. Uma caixa desligada é uma permissão que você não tem — e não se concede o que não se tem."
            dirty={mudou}
            footer={(
                <>
                    <Button variant="secondary" onClick={onFechar}>Cancelar</Button>
                    <Button loading={salvando} disabled={!mudou} onClick={() => { void salvar(); }}>Salvar</Button>
                </>
            )}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {erro && <Alert tone="danger" dismissible={false}>{erro}</Alert>}
                <Checkbox
                    checked={marcadas.includes('*')}
                    disabled={!minhas.includes('*')}
                    onChange={(v) => alternar('*', v)}
                    label="Acesso total"
                    description="Tudo o que existe e o que vier a existir. Só quem tem acesso total o concede."
                />
                {grupos.map((grupo) => (
                    <fieldset key={grupo} style={{ border: 0, margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
                        <legend style={{ fontSize: 11.5, fontWeight: 600, letterSpacing: 'var(--tracking-wide)', textTransform: 'uppercase', color: 'var(--text-tertiary)', marginBottom: 6 }}>
                            {grupo}
                        </legend>
                        {PERMISSOES_DA_PLATAFORMA.filter((p) => p.grupo === grupo).map((p) => (
                            <Checkbox
                                key={p.id}
                                checked={marcadas.includes(p.id)}
                                disabled={!operadorTem(minhas, p.id)}
                                onChange={(v) => alternar(p.id, v)}
                                label={p.rotulo}
                                description={p.id}
                            />
                        ))}
                    </fieldset>
                ))}
            </div>
        </Modal>
    );
}

/** O segundo fator da PRÓPRIA conta: cadastrar, confirmar, e só. */
function MeuMfa() {
    const aviso = useAviso();
    const [estado, setEstado] = useState<EstadoDoMfa | null>(null);
    const [cadastro, setCadastro] = useState<CadastroDeTotp | null>(null);
    const [codigo, setCodigo] = useState('');
    const [ocupado, setOcupado] = useState(false);
    const [erro, setErro] = useState<string | null>(null);

    const ler = useCallback(async () => {
        setEstado(await estadoDoMfa());
    }, []);

    useEffect(() => { void ler(); }, [ler]);

    const iniciar = async () => {
        setOcupado(true);
        setErro(null);
        try {
            setCadastro(await cadastrarTotp());
            setCodigo('');
        } catch (e) {
            setErro(e instanceof Error ? e.message : 'Não foi possível começar o cadastro.');
        } finally {
            setOcupado(false);
        }
    };

    const confirmar = async () => {
        if (!cadastro) return;
        setOcupado(true);
        setErro(null);
        try {
            await confirmarTotp(cadastro.fatorId, codigo);
            setCadastro(null);
            aviso({ title: 'Autenticador ativo', description: 'A partir de agora sua conta tem um segundo fator.' });
            await ler();
        } catch (e) {
            setErro(e instanceof Error ? e.message : 'Não foi possível confirmar.');
        } finally {
            setOcupado(false);
        }
    };

    /* Fator que não chegou a ser confirmado sai do servidor ao cancelar: deixá-lo
       lá seria um "MFA pendente" que ninguém vai terminar. */
    const cancelar = async () => {
        if (cadastro) {
            try {
                await descartarFator(cadastro.fatorId);
            } catch (e) {
                aviso({ title: 'O cadastro ficou pela metade no servidor', description: e instanceof Error ? e.message : '', tone: 'warning' });
            }
        }
        setCadastro(null);
    };

    const verificados = estado?.disponivel ? estado.fatores.filter((f) => f.verificado) : [];

    return (
        <Card title="Meu segundo fator (MFA)" subtitle="Protege a sua conta: é cadastrado por você, no seu celular" padding={18}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, fontSize: 13 }}>
                {erro && !cadastro && <Alert tone="danger" dismissible={false}>{erro}</Alert>}
                {estado === null && <p style={{ margin: 0, color: 'var(--text-secondary)' }}>Consultando o servidor de autenticação…</p>}
                {estado && !estado.disponivel && (
                    <Alert tone="neutral" dismissible={false} title="MFA indisponível aqui">{estado.motivo}</Alert>
                )}
                {estado?.disponivel && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                        {verificados.length > 0
                            ? <Badge tone="success" dot>{verificados.length === 1 ? 'Autenticador ativo' : `${verificados.length} autenticadores ativos`}</Badge>
                            : <Badge tone="warning" dot>Sem MFA</Badge>}
                        <Button size="sm" variant="secondary" loading={ocupado} iconLeft={<Icon name="key-round" size={14} />} onClick={() => { void iniciar(); }}>
                            {verificados.length > 0 ? 'Cadastrar outro autenticador' : 'Cadastrar autenticador'}
                        </Button>
                    </div>
                )}
            </div>

            <Modal
                open={cadastro !== null} onClose={() => { void cancelar(); }} width={460}
                title="Cadastrar autenticador"
                description="Leia o QR com o aplicativo autenticador e digite o código de seis dígitos que ele mostrar."
                footer={(
                    <>
                        <Button variant="secondary" onClick={() => { void cancelar(); }}>Cancelar</Button>
                        <Button loading={ocupado} disabled={codigo.replace(/\D/g, '').length !== 6} onClick={() => { void confirmar(); }}>Confirmar</Button>
                    </>
                )}
            >
                {cadastro && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, alignItems: 'stretch' }}>
                        {erro && <Alert tone="danger" dismissible={false}>{erro}</Alert>}
                        <img src={cadastro.qrCode} alt="QR do autenticador" style={{ width: 180, height: 180, alignSelf: 'center' }} />
                        <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-secondary)' }}>
                            Sem câmera? Digite este segredo no aplicativo: <code style={{ wordBreak: 'break-all' }}>{cadastro.segredo}</code>
                        </p>
                        <FormField label="Código do aplicativo" htmlFor="mfa-codigo" required>
                            <Input id="mfa-codigo" value={codigo} onChange={setCodigo} placeholder="000000" />
                        </FormField>
                    </div>
                )}
            </Modal>
        </Card>
    );
}
